// Lichte geluidseffecten via de Web Audio API (geen audiobestanden nodig).
// Aan/uit wordt onthouden in localStorage (la_sound). Standaard AAN.

let _muted = localStorage.getItem("la_sound") === "off";
let _ctx = null;

function ctx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) _ctx = new AC();
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

export function isMuted() { return _muted; }
export function setMuted(m) {
  _muted = !!m;
  localStorage.setItem("la_sound", _muted ? "off" : "on");
}
export function toggleMuted() { setMuted(!_muted); return _muted; }

// Eén toon met een zachte volume-envelope.
function tone(freq, startOffset, dur, { type = "sine", gain = 0.18 } = {}) {
  const ac = ctx();
  if (!ac) return;
  const t0 = ac.currentTime + startOffset;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function play(notes) {
  if (_muted) return;
  try { notes(); } catch { /* audio mag nooit de UI breken */ }
}

export const sfx = {
  click: () => play(() => tone(440, 0, 0.07, { type: "triangle", gain: 0.10 })),
  select: () => play(() => tone(620, 0, 0.08, { type: "triangle", gain: 0.12 })),
  correct: () => play(() => { tone(660, 0, 0.10, { type: "sine" }); tone(880, 0.08, 0.14, { type: "sine" }); }),
  wrong: () => play(() => { tone(220, 0, 0.18, { type: "sawtooth", gain: 0.10 }); tone(160, 0.10, 0.20, { type: "sawtooth", gain: 0.10 }); }),
  coin: () => play(() => { tone(988, 0, 0.06, { type: "square", gain: 0.10 }); tone(1319, 0.06, 0.12, { type: "square", gain: 0.10 }); }),
  // Vrolijke opstijgende fanfare bij een goed resultaat.
  win: () => play(() => {
    [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 0.11, 0.22, { type: "triangle", gain: 0.16 }));
  }),
  badge: () => play(() => { [784, 988, 1319].forEach((f, i) => tone(f, i * 0.09, 0.2, { type: "sine", gain: 0.16 })); }),
  beep: () => play(() => tone(440, 0, 0.13, { type: "square", gain: 0.12 })),
  go: () => play(() => { tone(660, 0, 0.10, { type: "square", gain: 0.14 }); tone(1047, 0.09, 0.24, { type: "square", gain: 0.14 }); }),
  // Harde knal (crash) — ruisexplosie + lage dreun.
  bang: () => play(() => {
    const ac = ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const dur = 0.38;
    const buf = ac.createBuffer(1, Math.max(1, Math.floor(ac.sampleRate * dur)), ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(1400, t0);
    lp.frequency.exponentialRampToValueAtTime(180, t0 + dur);
    const g = ac.createGain();
    g.gain.setValueAtTime(0.55, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(lp).connect(g).connect(ac.destination);
    src.start(t0);
    src.stop(t0 + dur);
    tone(85, 0, 0.32, { type: "sawtooth", gain: 0.3 });
  }),
  // Snelle "whoosh" in de bochten van de knikkerbaan.
  whoosh: () => play(() => {
    const ac = ctx();
    if (!ac) return;
    const t0 = ac.currentTime;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(180, t0);
    o.frequency.exponentialRampToValueAtTime(760, t0 + 0.16);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.07, t0 + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.2);
    o.connect(g).connect(ac.destination);
    o.start(t0);
    o.stop(t0 + 0.22);
  }),
};

// ---- Loopende achtergrond-tune (eigen chiptune, IP-veilig) voor Digger 🚜 ----
let _musicTimer = null;
let _musicOn = false;

// Vrolijke bouncy loop (Am - G - F - G), 8e noten. 0 = rust.
const _DIGGER_MELODY = [
  440, 523, 659, 523, 440, 523, 659, 523, // Am
  392, 494, 587, 494, 392, 494, 587, 494, // G
  349, 440, 523, 440, 349, 440, 523, 440, // F
  392, 494, 587, 784, 659, 523, 494, 392, // G -> turnaround
];
const _DIGGER_BASS = [110, 98, 87.31, 98]; // A2, G2, F2, G2 — één per maat (8 noten)

export const music = {
  start() {
    if (_muted) return;
    this.stop();
    if (!ctx()) return;
    _musicOn = true;
    const bpm = 138;
    const beat = 60 / bpm; // sec per 8e noot
    let i = 0;
    const step = () => {
      if (!_musicOn || _muted) { this.stop(); return; }
      const f = _DIGGER_MELODY[i % _DIGGER_MELODY.length];
      if (f > 0) tone(f, 0, beat * 0.85, { type: "square", gain: 0.05 });
      if (i % 8 === 0) {
        const bf = _DIGGER_BASS[(Math.floor(i / 8)) % _DIGGER_BASS.length];
        tone(bf, 0, beat * 7, { type: "triangle", gain: 0.06 });
      }
      i++;
      _musicTimer = setTimeout(step, beat * 1000);
    };
    step();
  },
  stop() {
    _musicOn = false;
    if (_musicTimer) { clearTimeout(_musicTimer); _musicTimer = null; }
  },
};
